function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import _ from 'lodash';
import { argumentsObjectFromField } from '@apollo/client/utilities';
import { bucketNodesByPath, truncateIncludeOnly, ROOT_PATH } from '@stripe-internal/babel-plugin-data-graphql-helpers';
import { rejectTypenameDeep } from './rejectTypenameDeep';
import stringify from './stringify';
const leadingSlashRegex = /^\//;
const pathVariableRegex = /:([^/]*)/g;
const snakeCaseSkipList = new Set([
    'line1',
    'line2',
    'last4'
]);
const ignoredVariables = {
    __typename: true
};
const PAYMENTS_LIST_AVOID_FIELDS = new Set([
    'data.unified_instrument'
]);
// Fields which are expensive to expand should be added here to override
// the default behavior of truncating fields with the most nodes.
const FIELDS_TO_AVOID_BY_OPERATION_NAME = {
    BapiPaymentsTableListQuery: PAYMENTS_LIST_AVOID_FIELDS,
    BentoPaymentsTableListQuery: PAYMENTS_LIST_AVOID_FIELDS
};
const getUrlTokenMatches = (path)=>[
        ...path.matchAll(pathVariableRegex)
    ];
const parsePathWithVariables = (path, input)=>{
    return getUrlTokenMatches(path).reduce((acc, pathVariableMatch)=>{
        const pathMatch = pathVariableMatch[0];
        const pathVariable = pathVariableMatch[1];
        const matchingArg = input[pathVariable];
        return {
            parsedPath: acc.parsedPath.replace(pathMatch, encodeURIComponent(matchingArg)),
            pathVariableKeys: acc.pathVariableKeys.concat(pathVariable)
        };
    }, {
        parsedPath: path,
        pathVariableKeys: []
    });
};
export const bodyBuilder = (params)=>{
    const { args, '@rest': { runtimePath: path } } = params;
    const keysToOmit = {};
    Object.keys(ignoredVariables).forEach((key)=>{
        keysToOmit[key] = true;
    });
    getUrlTokenMatches(path).forEach(([, key])=>{
        keysToOmit[key] = true;
    });
    if (args === null || args === void 0 ? void 0 : args.input) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return Object.keys(args.input).reduce((acc, key)=>{
            if (args.input && !keysToOmit[key]) {
                return _object_spread_props(_object_spread({}, acc), {
                    [key]: rejectTypenameDeep(args.input[key])
                });
            } else {
                return acc;
            }
        }, {});
    } else {
        return {};
    }
};
const buildRequestURL = (restDirective, input, predicateVariables, query, observabilityFns)=>{
    const { runtimePath, method } = restDirective;
    const { parsedPath, pathVariableKeys } = parsePathWithVariables(runtimePath.replace(leadingSlashRegex, ''), input);
    const requestQueryParams = _object_spread({}, query);
    // Appending additional input args as GET / DELETE query params.
    if (method === 'GET' || method === 'DELETE') {
        Object.keys(input).forEach((key)=>{
            if (!pathVariableKeys.includes(key) && !predicateVariables.has(key) && !ignoredVariables[key]) {
                let newKey = key;
                if (!snakeCaseSkipList.has(key)) {
                    if (/\d/.test(key)) {
                        var _observabilityFns_errors;
                        observabilityFns === null || observabilityFns === void 0 ? void 0 : (_observabilityFns_errors = observabilityFns.errors) === null || _observabilityFns_errors === void 0 ? void 0 : _observabilityFns_errors.warning('request query param has number but is being snake-cased', {
                            project: 'sail_core',
                            extras: {
                                key
                            }
                        });
                    }
                    newKey = _.snakeCase(key);
                }
                requestQueryParams[newKey] = input[key];
            }
        });
    }
    const queryString = stringify(requestQueryParams);
    return `${parsedPath}${queryString ? '?' : ''}${queryString}`;
};
const mapLookupMapToArgument = (nodeLookupMap)=>{
    const args = [];
    Object.keys(nodeLookupMap).forEach((path)=>{
        args.push({
            parentPath: path,
            nodes: nodeLookupMap[path]
        });
    });
    return args;
};
const evaluatePredicates = (accumulator, node, input, parentExpandParam = '')=>{
    // Evaluate the current node.
    let appendCurrentNodeInfo = false;
    if (node.type === 'include') {
        appendCurrentNodeInfo = input[node.variable];
    } else if (node.type === 'skip') {
        appendCurrentNodeInfo = !input[node.variable];
    }
    if (appendCurrentNodeInfo) {
        accumulator.includeOnly = accumulator.includeOnly.concat(node.includeOnly);
        accumulator.fragments = accumulator.fragments.concat(node.fragments);
        accumulator.nodeLookupMap = accumulator.nodeLookupMap.concat(mapLookupMapToArgument(bucketNodesByPath(node.includeOnly)));
        // Only expand children if the current predicate node is true.
        if (node.children.length) {
            // End of the expand param, but there are more predicate nodes to be
            // evaluated.
            if (!node.expand && parentExpandParam) {
                accumulator.expands.push(parentExpandParam);
            }
            node.children.forEach((childNode)=>{
                evaluatePredicates(accumulator, childNode, input, node.expand);
            });
        } else {
            const expand = node.expand || parentExpandParam;
            if (expand) {
                accumulator.expands.push(expand);
            }
        }
    } else if (parentExpandParam) {
        // If the current predicate node is evaluated to false but it's parents has
        // an expand param.
        accumulator.expands.push(parentExpandParam);
    }
};
const extractParamsDirective = (operationName, directives, input, isDashboardOnlyAPIKey)=>{
    const pathParams = {
        expands: [],
        includeOnly: [],
        fragments: [],
        nodeLookupMap: [],
        predicateVariables: []
    };
    const paramsDirective = directives === null || directives === void 0 ? void 0 : directives.find(({ name })=>name.value === 'params');
    if (paramsDirective) {
        const argumentsObject = argumentsObjectFromField(paramsDirective);
        // Base params.
        pathParams.expands = argumentsObject.expand;
        pathParams.fragments = argumentsObject.fragments;
        pathParams.nodeLookupMap = argumentsObject.nodeLookupMap;
        // The `include_only` request param is only supported for dashboard-only API keys.
        if (isDashboardOnlyAPIKey) {
            pathParams.includeOnly = argumentsObject.includeOnly;
        }
        // Conditional params.
        if (!_.isEmpty(argumentsObject.predicates)) {
            argumentsObject.predicates.ROOT.children.forEach((topLevelNode)=>{
                evaluatePredicates(pathParams, topLevelNode, input);
            });
            pathParams.predicateVariables = argumentsObject.predicates.ROOT.variables;
        }
    } else {
        throw new Error(`[client-proxy]: @params is not defined in: ${operationName}`);
    }
    return pathParams;
};
const buildFragmentDefinitionsLookup = (fragmentDefinitions)=>{
    const result = {};
    fragmentDefinitions.forEach((fragmentDefinition)=>{
        result[fragmentDefinition.name.value] = fragmentDefinition;
    });
    return result;
};
/**
 * At a high level, this function return all permutations of possible paths
 * that leads to the current fragment. For instance, given a fragmentPaths:
 *
 * [
 *   // We have an array of array here because there are two possible paths to
 *   // the first fragment, ie. the first fragment is being used at both
 *   // `customer.data` and `account.data` within the query
 *   [customer.data, account.data],
 *   [creditCard],
 *   [balance.amount],
 * ]
 *
 * The result would be:
 *
 * [
 *   customer.data.creditCard.balance.amount,
 *   account.data.creditCard.balance.amount,
 * ]
 *
 * Algorithm origin: https://stackoverflow.com/questions/5751091/permutations-of-an-array-of-arrays-of-strings
 */ const getAllPathPermutations = (fragmentPaths)=>{
    const permutations = [];
    const paths = fragmentPaths.map(({ paths })=>paths);
    let iterator = 0;
    // eslint-disable-next-line no-constant-condition
    while(true){
        const pick = [];
        let num = iterator++;
        paths.forEach((path)=>{
            const r = num % path.length;
            num = (num - r) / path.length;
            pick.push(path[r]);
        });
        if (num > 0) {
            break;
        }
        permutations.push(pick.filter(Boolean).join('.'));
    }
    return permutations;
};
const getFullPath = (parentPath, param)=>{
    if (parentPath) {
        return `${parentPath}.${param}`;
    }
    return param;
};
class ParamsAccumulator {
    _appendToNodeLookupMap(parentPath, nodeLookupMapArgument) {
        nodeLookupMapArgument.forEach(({ parentPath: path, nodes })=>{
            let fullPath;
            if (parentPath === ROOT_PATH) {
                // Root level lookup map.
                fullPath = path;
            } else {
                // Each fragment.
                fullPath = path === ROOT_PATH ? parentPath : `${parentPath}.${path}`;
            }
            const existingNodes = this.nodeLookupMap[fullPath] || [];
            this.nodeLookupMap[fullPath] = Array.from(new Set([
                ...existingNodes,
                ...nodes
            ]));
        });
    }
    append(namespace, params) {
        params.predicateVariables.forEach((variable)=>{
            this.predicateVariables.add(variable);
        });
        params.includeOnly.forEach((include)=>{
            this.includeOnly.add(getFullPath(namespace, include));
        });
        params.expands.forEach((expand)=>{
            const fullPath = getFullPath(namespace, expand);
            const toRemove = [];
            this.expands.forEach((eachExpand)=>{
                // We are doing BFS so we are looking for sub-expands to remove.
                if (fullPath.startsWith(`${eachExpand}.`)) {
                    toRemove.push(eachExpand);
                }
            });
            this.expands.add(fullPath);
            toRemove.forEach(this.expands.delete, this.expands);
        });
        this._appendToNodeLookupMap(namespace || ROOT_PATH, params.nodeLookupMap);
    }
    constructor(params){
        _define_property(this, "includeOnly", void 0);
        _define_property(this, "expands", void 0);
        _define_property(this, "predicateVariables", void 0);
        _define_property(this, "nodeLookupMap", void 0);
        this.includeOnly = new Set(params.includeOnly);
        this.expands = new Set(params.expands);
        this.predicateVariables = new Set(params.predicateVariables);
        this.nodeLookupMap = {};
        this._appendToNodeLookupMap(ROOT_PATH, params.nodeLookupMap);
    }
}
const mergeFragmentParams = (input, baseOperationName, params, currentPath, fragmentDefinitionLookup, accumulator, isDashboardOnlyAPIKey)=>{
    getAllPathPermutations(currentPath).forEach((pathToFragment)=>{
        accumulator.append(pathToFragment, params);
    });
    if (!_.isEmpty(params.fragments)) {
        params.fragments.forEach((fragment)=>{
            const fragmentDefinition = fragmentDefinitionLookup[fragment.name];
            const fragmentParams = extractParamsDirective(`${baseOperationName} > ${fragment.name}`, fragmentDefinition.directives, input, isDashboardOnlyAPIKey);
            mergeFragmentParams(input, baseOperationName, fragmentParams, [
                ...currentPath,
                fragment
            ], fragmentDefinitionLookup, accumulator, isDashboardOnlyAPIKey);
        });
    }
};
const getOperationSelection = (selections, queryName)=>selections.find((selection)=>{
        if (selection.kind === 'Field') {
            var _selection_alias;
            return queryName === ((_selection_alias = selection.alias) === null || _selection_alias === void 0 ? void 0 : _selection_alias.value) || queryName === selection.name.value;
        }
        return false;
    });
export const runtimePathBuilder = (operationName, restDirective, operationDefinition, fragmentDefinitions, input, isDashboardOnlyAPIKey, observabilityFns, convertGetQueryToPostBody)=>{
    const operationSelection = getOperationSelection(operationDefinition.selectionSet.selections, restDirective.queryName);
    const operationParams = extractParamsDirective(operationName, // Only the top level directives of the operation since we already saved
    // all the path param info from the operation with @params during the build
    // time.
    operationSelection === null || operationSelection === void 0 ? void 0 : operationSelection.directives, input, isDashboardOnlyAPIKey);
    const fragmentDefinitionsLookup = buildFragmentDefinitionsLookup(fragmentDefinitions);
    const accumulator = new ParamsAccumulator(operationParams);
    mergeFragmentParams(input, operationName, operationParams, [], fragmentDefinitionsLookup, accumulator, isDashboardOnlyAPIKey);
    const originalIncludeOnly = Array.from(accumulator.includeOnly);
    var _FIELDS_TO_AVOID_BY_OPERATION_NAME_operationName;
    const includeOnly = convertGetQueryToPostBody ? originalIncludeOnly // We use POST to avoid truncating, letting us add more includeOnly contents
     : truncateIncludeOnly(originalIncludeOnly, accumulator.nodeLookupMap, (_FIELDS_TO_AVOID_BY_OPERATION_NAME_operationName = FIELDS_TO_AVOID_BY_OPERATION_NAME[operationName]) !== null && _FIELDS_TO_AVOID_BY_OPERATION_NAME_operationName !== void 0 ? _FIELDS_TO_AVOID_BY_OPERATION_NAME_operationName : new Set());
    const truncatedFields = includeOnly.filter((param)=>!accumulator.includeOnly.has(param));
    if (truncatedFields.length) {
        var _observabilityFns_analytics;
        observabilityFns === null || observabilityFns === void 0 ? void 0 : (_observabilityFns_analytics = observabilityFns.analytics) === null || _observabilityFns_analytics === void 0 ? void 0 : _observabilityFns_analytics.track('rest_link_truncation', {
            project: 'sail_core',
            operation_name: operationName,
            operation_truncated_fields: truncatedFields.join(',')
        });
    }
    return buildRequestURL(restDirective, input, accumulator.predicateVariables, {
        expand: Array.from(accumulator.expands),
        include_only: [
            includeOnly.join(',')
        ].filter(Boolean)
    }, observabilityFns);
};

//# sourceMappingURL=builders.js.map